import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBackIos';
import { makeStyles } from '@material-ui/core';
import { designTokens } from '../../styles/designTokens';
import PhoneInputStep from './PhoneInputStep';
import PatientSelectionStep from './PatientSelectionStep';
import CreatePatientStep from './CreatePatientStep';
import { getPatientsForDoctorOrdering, setCreatePatientByDoctor } from '@cabinet-packages/api/api';

const useStyles = makeStyles((theme) => ({
    dialog: {},
    dialogPaper: {
        borderRadius: designTokens.borderRadius.modal,
        padding: designTokens.spacing.xl,
        margin: designTokens.spacing.xxl,
        '@media (max-width: 600px)': {
            minWidth: 'calc(100vw - 32px) !important',
            maxWidth: 'calc(100vw - 32px) !important',
            margin: designTokens.spacing.lg,
            padding: '20px',
        }
    },
    header: {
        position: 'relative',
        minHeight: '40px',
        marginBottom: designTokens.spacing.md,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: designTokens.colors.text.secondary,
        padding: '8px 12px',
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        fontSize: '14px',
        fontWeight: 400,
        borderRadius: '4px',
        fontFamily: designTokens.fontFamily,
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '@media (max-width: 600px)': {
            padding: '4px 8px',
            fontSize: '13px',
        }
    },
    backIcon: {
        fontSize: '20px',
        '@media (max-width: 600px)': {
            fontSize: '18px',
        }
    },
    closeButton: {
        color: designTokens.colors.text.secondary,
        padding: '4px',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        }
    },
    content: {
        padding: '0 !important',
    },
    warningText: {
        ...designTokens.typography.body,
        lineHeight: '100%',
        color: designTokens.colors.red.main,
        margin: `0 0 ${designTokens.spacing.xl}`,
        padding: '0',
        backgroundColor: 'transparent',
        '@media (max-width: 600px)': {
            fontSize: '14px',
            lineHeight: '140%',
        }
    }
}));

const AddPatientModal = ({
    open,
    onClose,
    onPatientSelect
}) => {
    const classes = useStyles();
    const [step, setStep] = useState(1);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [previousStep, setPreviousStep] = useState(null);

    const handleClose = () => {
        setStep(1);
        setPhoneNumber('');
        setPatients([]);
        setNotFound(false);
        setPreviousStep(null);
        onClose();
    };

    const handleBack = () => {
        if (step === 2) {
            setStep(1);
        } else if (step === 3) {
            setStep(previousStep || 1);
            setPreviousStep(null);
        }
    };

    const handlePhoneSubmit = async (phone) => {
        setPhoneNumber(phone);
        setLoading(true);
        setNotFound(false);

        try {
            const cleanPhone = phone.replace(/[[()\s\-]/g, '');

            const result = await getPatientsForDoctorOrdering(cleanPhone);

            if (result && result.length > 0) {
                const formattedPatients = result.map(patient => ({
                    id: patient.id,
                    fullname: `${patient.lastname} ${patient.firstname} ${patient.fathername || ''}`.trim(),
                    firstname: patient.firstname,
                    lastname: patient.lastname,
                    fathername: patient.fathername,
                    sex: patient.sex,
                    birthday: patient.birthday,
                    phone: patient.phone,
                    email: patient.email,
                }));

                setPatients(formattedPatients);
                setNotFound(false);
                setStep(2);
            } else {
                setPatients([]);
                setNotFound(true);
            }
        } catch (error) {
            console.error('Error fetching patients:', error);
            setPatients([]);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const handlePatientSelect = (patient) => {
        onPatientSelect(patient, patients);
        handleClose();
    };

    const handleCreateNewPatient = () => {
        setPreviousStep(step);
        setStep(3);
    };

    const handlePatientCreate = async (patientData) => {
        setLoading(true);

        try {
            const lastname = patientData.lastName.trim();
            const firstname = patientData.firstName.trim();
            const fathername = patientData.middleName ? patientData.middleName.trim() : '';
            const birthday = patientData.birthDate ? patientData.birthDate.toISOString().slice(0, 10) : null;
            const sex = patientData.gender === 'male' ? 'M' : 'F';
            const phone = phoneNumber.replace(/[[()\s\-+]/g, '');
            const email = patientData.email || '';

            const result = await setCreatePatientByDoctor(
                lastname,
                firstname,
                fathername,
                birthday,
                sex,
                phone,
                email
            );

            if (result && result.id) {
                const newPatient = {
                    id: result.id,
                    fullname: `${lastname} ${firstname} ${fathername}`.trim(),
                    firstname: firstname,
                    lastname: lastname,
                    fathername: fathername,
                    sex: sex,
                    birthday: birthday,
                    phone: phone,
                    email: email,
                };

                const updatedPatients = [...patients, newPatient];

                onPatientSelect(newPatient, updatedPatients);
                handleClose();
            }
        } catch (error) {
            console.error('Error creating patient:', error);
        } finally {
            setLoading(false);
        }
    };

    const modalWidth = step === 3 ? '500px' : '420px';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            className={classes.dialog}
            classes={{ paper: classes.dialogPaper }}
            PaperProps={{
                style: {
                    minWidth: modalWidth,
                    maxWidth: modalWidth,
                }
            }}
        >
            <div className={classes.header}>
                {step > 1 && (
                    <button onClick={handleBack} className={classes.backButton}>
                        <ArrowBackIcon className={classes.backIcon} />
                        <span>Повернутись</span>
                    </button>
                )}
                <div></div>
                <IconButton onClick={handleClose} className={classes.closeButton}>
                    <CloseIcon />
                </IconButton>
            </div>

            <DialogContent className={classes.content}>
                {step === 1 && (
                    <>
                        <div className={classes.warningText}>
                            {notFound
                                ? `Пацієнта з номером ${phoneNumber} не знайдено. Перевірте номер або додайте нового пацієнта.`
                                : 'Увага! Аналізи, що входять в одне замовлення, потрібно здати під час одного відвідування відділення.'
                            }
                        </div>
                        <PhoneInputStep
                            onSubmit={handlePhoneSubmit}
                            loading={loading}
                            notFound={notFound}
                            phoneNumber={phoneNumber}
                            onCreateNew={handleCreateNewPatient}
                        />
                    </>
                )}

                {step === 2 && (
                    <PatientSelectionStep
                        phoneNumber={phoneNumber}
                        patients={patients}
                        onSelect={handlePatientSelect}
                        onCreateNew={handleCreateNewPatient}
                    />
                )}

                {step === 3 && (
                    <CreatePatientStep
                        phoneNumber={phoneNumber}
                        onCreate={handlePatientCreate}
                        loading={loading}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AddPatientModal;
